import {
  FunctionField,
  ReferenceField,
  ShowContextProvider,
  TextField,
  Title,
  useEditController,
  useGetMany,
} from "react-admin";
import { useParams } from "react-router-dom";
import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { PageTopBreadcrumb } from "../../components/PageTopBreadcrumb";
import { IdChip } from "../../components/IdChip";
import { RoleStatusChip } from "../../components/RoleStatusChip";
import { showLabelSx } from "../../components/listStyles";
import { colors } from "../../data/tokens";
import {
  MemberLinks,
  RolePeriod,
  mutedTextSx,
  roleTypeLabel,
  sphereLabel,
} from "./positionRoleMeta";

const valueSx = { fontSize: 13.5, color: colors.textStrong };

const InfoItem = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Box>
    <Typography sx={showLabelSx}>{label}</Typography>
    <Box sx={{ marginTop: 0.5 }}>{children}</Box>
  </Box>
);

// Na ficha a API devolve os sócios só como lista de IDs
const RoleMembers = ({ ids }: { ids?: string[] }) => {
  const { data, isLoading } = useGetMany("users/crud", { ids: ids || [] });
  if (!ids || ids.length === 0) {
    return <Typography sx={mutedTextSx}>Nenhum sócio vinculado</Typography>;
  }
  if (isLoading) return <Typography sx={mutedTextSx}>Carregando…</Typography>;
  return <MemberLinks members={data} />;
};

// Ficha no padrão do painel: cargo em destaque com o status colorido,
// dados do mandato lado a lado, sócios com link para o cadastro e o
// identificador técnico copiável. A versão antiga mostrava o tipo como
// código ("CARGOS_FUNCOES") e só exibia período e datas quando havia
// "cargo na comissão" — ou seja, quase nunca.
export const UserPositionRoleShow = () => {
  const { id } = useParams();
  const controller: any = useEditController({
    resource: "user-position-roles/crud",
    id,
  });
  const record = controller?.record;

  return (
    <ShowContextProvider value={controller}>
      <Title title="Cargos e Funções" />
      <PageTopBreadcrumb
        title="Cargos e Funções"
        firstPath="/#/user-position-roles/crud"
        secondTitle="Visualizando"
        thirdTitle={record?.name}
        actionType="edit"
      />
      {record && (
        <Card sx={{ marginBottom: 3 }}>
          {/* Box em vez de CardContent: o App.css zera o padding de todo
              .MuiCardContent-root com !important */}
          <Box sx={{ padding: { xs: 2.5, md: 4 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: colors.primaryDark,
                }}
              >
                {record.name}
              </Typography>
              {record.roleStatusId && (
                <ReferenceField
                  source="roleStatusId"
                  reference="user-role-status/crud"
                  link={false}
                >
                  <FunctionField
                    render={(status: any) => (
                      <RoleStatusChip name={status?.name} />
                    )}
                  />
                </ReferenceField>
              )}
            </Box>
            {record.roleType && (
              <Typography sx={{ ...mutedTextSx, fontSize: 13, marginTop: 0.5 }}>
                {roleTypeLabel(record.roleType)}
              </Typography>
            )}

            <Divider sx={{ marginY: 2.5 }} />

            {/* Campos vazios ficam de fora */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 4,
                alignItems: "flex-start",
              }}
            >
              <InfoItem label="Período">
                <RolePeriod record={record} />
              </InfoItem>
              {!record.roleStatusId && (
                <InfoItem label="Status">
                  <Typography sx={mutedTextSx}>Não informado</Typography>
                </InfoItem>
              )}
              {record.technicalDivisionId && (
                <InfoItem label="Divisão Técnica">
                  <ReferenceField
                    source="technicalDivisionId"
                    reference="technical-divisions/crud"
                    link="show"
                  >
                    <TextField
                      source="name"
                      sx={{ ...valueSx, fontWeight: 600 }}
                    />
                  </ReferenceField>
                </InfoItem>
              )}
              {record.secondaryRole && (
                <InfoItem label="Cargo na comissão">
                  <Typography sx={valueSx}>{record.secondaryRole}</Typography>
                </InfoItem>
              )}
              {record.sphere && (
                <InfoItem label="Esfera de atuação">
                  <Typography sx={valueSx}>
                    {sphereLabel(record.sphere)}
                  </Typography>
                </InfoItem>
              )}
              <InfoItem label="Identificador técnico">
                <IdChip value={record.id} />
              </InfoItem>
            </Box>

            <Divider sx={{ marginY: 2.5 }} />

            <InfoItem label="Sócios">
              <RoleMembers ids={record.users} />
            </InfoItem>
          </Box>
        </Card>
      )}
    </ShowContextProvider>
  );
};
